import { RolodexIndication } from 'features/indicationTriage'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import { ctpEntryToLayer } from 'features/libra/stepComponents/ffs/ffsCtpMarkup'
import { indicationToRectLayer } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { isFfsLtaPathMarkupLayer } from 'features/libra/stepComponents/ffs/ffsLtaPathMarkup'
import {
  rulerEntryToLayer,
  rulerQueryForIndications,
} from 'features/libra/stepComponents/ffs/ffsRulerMarkup'
import { useFfsComponentCdfs } from 'features/libra/stepComponents/ffs/useFfsComponentCdfs'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useEffect, useMemo } from 'react'
import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoEntry,
  GeckoMarkupLayer,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'

interface UseFfsMarkupLayersArgs {
  indications: ReadonlyArray<RolodexIndication>
  ltaSetId: string | undefined
  component: GeckoComponent | undefined
  inspectionId: string | undefined
  selectedIndicationId?: string | null
}

interface UseFfsMarkupLayersResult {
  /** Refreshes every Rolodex query owned by this hook. */
  forceRefresh: () => Promise<unknown>
}

interface BuildFfsMarkupLayersArgs {
  indications: ReadonlyArray<RolodexIndication>
  selectedIndicationId?: string | null
  ctps: ReadonlyArray<GeckoEntry>
  rulers: ReadonlyArray<GeckoEntry>
  cdfsById: ReadonlyMap<string, GeckoComponentDataFrame>
  primaryCdf: GeckoComponentDataFrame | undefined
  component: GeckoComponent | undefined
  flawMarkupLayers: ReadonlyArray<GeckoMarkupLayer>
  componentBounds?: GeckoComponentRect
  /**
   * The full CDF family for this (component, inspection) -- used to
   * grid-match a legacy CTP (no `component-data-frame` tag) to the CDF it
   * was actually generated against, rather than falling all the way back to
   * `primaryCdf`. See `resolveCtpCdf`.
   */
  cdfFamily: ReadonlyArray<GeckoComponentDataFrame>
}

const lengthUnit = GeckoMeasurementUnit.Millimeters

const classificationFor = (indication: RolodexIndication): string | undefined =>
  indication.classificationInfo?.classification

export function buildFfsMarkupLayers({
  indications,
  selectedIndicationId,
  ctps,
  rulers,
  cdfsById,
  primaryCdf,
  component,
  flawMarkupLayers,
  componentBounds,
  cdfFamily,
}: BuildFfsMarkupLayersArgs): GeckoMarkupLayer[] {
  const cdfByIndicationId = new Map<
    string,
    GeckoComponentDataFrame | undefined
  >()
  for (const ctp of ctps) {
    const indicationId = ctp.tags.indication as string | undefined
    if (!indicationId) continue
    const ctpCdfId = ctp.tags['component-data-frame'] as string | undefined
    cdfByIndicationId.set(
      indicationId,
      (ctpCdfId ? cdfsById.get(ctpCdfId) : undefined) ?? primaryCdf
    )
  }

  /**
   * Resolves the CDF to pass into `ctpEntryToLayer`. Order:
   *  1. The CTP's own `component-data-frame` tag, if any.
   *  2. Legacy grid match: among `cdfFamily` CDFs sharing this CTP's
   *     `component-dataset`, the one whose wrap-axis bin count equals the
   *     CTP's `period_bins` -- i.e. the grid the profile was actually
   *     generated against.
   *  3. `primaryCdf` (today's behavior for anything still unresolved).
   * `ctpEntryToLayer` now prefers the CTP entry's own explicit resolution
   * fields when present, so this resolution only matters for legacy
   * entries that predate that contract.
   */
  const resolveCtpCdf = (ctpEntry: GeckoEntry) => {
    const ctpCdfId = ctpEntry.tags['component-data-frame'] as string | undefined
    const tagged = ctpCdfId ? cdfsById.get(ctpCdfId) : undefined
    if (tagged) return tagged

    const { period_bins, wrap_axis } = ctpEntry.data as {
      period_bins?: number
      wrap_axis?: 'x' | 'y'
    }
    const ctpDatasetId = ctpEntry.tags['component-dataset'] as
      | string
      | undefined
    if (ctpDatasetId && typeof period_bins === 'number') {
      const gridMatch = cdfFamily.find(
        (cdf) =>
          cdf.tags['component-dataset'] === ctpDatasetId &&
          (wrap_axis === 'y'
            ? cdf.data.binning.y_bin_count === period_bins
            : cdf.data.binning.x_bin_count === period_bins)
      )
      if (gridMatch) return gridMatch
    }

    return primaryCdf
  }

  // The CTP's LTA arc is the matching indication's folded selection --
  // `ctpEntryToLayer` reads it to break/wrap segments that would otherwise
  // cross outside a wrapped/multi-arc LTA (see ffsCtpMarkup.ts).
  const selectionByIndicationId = new Map<
    string,
    RolodexIndication['selection']
  >()
  for (const indication of indications) {
    selectionByIndicationId.set(indication.id, indication.selection)
  }

  return [
    ...flawMarkupLayers,
    ...indications.flatMap((i) =>
      indicationToRectLayer(
        i,
        classificationFor(i),
        i.id === selectedIndicationId,
        componentBounds
      )
    ),
    ...ctps.flatMap((ctp, i) => {
      const indicationId = ctp.tags.indication as string | undefined
      const selection = indicationId
        ? selectionByIndicationId.get(indicationId)
        : undefined
      return ctpEntryToLayer(ctp, resolveCtpCdf(ctp), selection, i + 1)
    }),
    ...rulers.flatMap((r) => {
      const indicationId = r.tags.indication ?? undefined
      const cdf = indicationId
        ? (cdfByIndicationId.get(indicationId) ?? primaryCdf)
        : primaryCdf
      return cdf && component
        ? rulerEntryToLayer(r, cdf, component, lengthUnit)
        : []
    }),
  ]
}

export function useFfsMarkupLayers({
  indications,
  ltaSetId,
  component,
  inspectionId,
  selectedIndicationId,
}: UseFfsMarkupLayersArgs): UseFfsMarkupLayersResult {
  const { primaryCdf, setFfsMarkupLayers } = useFfsWorkspaceState()
  const { componentModel } = useFfsTicketContext()
  const componentBounds = componentModel?.componentBounds

  // The full CDF family for this (component, inspection) -- used to
  // grid-match a legacy CTP (no `component-data-frame` tag) to its true
  // resolution. SWR dedups this against any other panel that already
  // fetched it (e.g. `FfsAssetVizPanel`).
  const { cdfs: cdfFamily } = useFfsComponentCdfs({
    componentId: component?.id,
    inspectionId,
  })

  const indicationIds = useMemo(
    () => indications.map((i) => i.id),
    [indications]
  )

  const { entries: ctpEntries, forceRefresh: refreshCtps } =
    useRolodexEntriesWithSyncedRefreshes(
      indicationIds.length > 0
        ? {
            type: RolodexEntryType.FFS_CRITICAL_THICKNESS_PROFILE,
            tags: { [RolodexEntryType.INDICATION]: indicationIds },
          }
        : undefined
    )
  const ctps = ctpEntries

  const { entries: rulerEntries, forceRefresh: refreshRulers } =
    useRolodexEntriesWithSyncedRefreshes(
      rulerQueryForIndications(indicationIds)
    )
  const rulers = rulerEntries

  const ctpCdfIds = useMemo(
    () =>
      Array.from(
        new Set(
          ctps
            .map(
              (ctp) => ctp.tags['component-data-frame'] as string | undefined
            )
            .filter((id): id is string => Boolean(id))
        )
      ),
    [ctps]
  )
  const { entries: cdfEntries, forceRefresh: refreshCtpCdfs } =
    useRolodexEntriesWithSyncedRefreshes(
      ctpCdfIds.length > 0
        ? { type: RolodexEntryType.COMPONENT_DATA_FRAME, ids: ctpCdfIds }
        : undefined
    )
  const cdfsById = useMemo(() => {
    const map = new Map<string, GeckoComponentDataFrame>()
    for (const cdf of cdfEntries as ReadonlyArray<GeckoComponentDataFrame>) {
      map.set(cdf.id, cdf)
    }
    return map
  }, [cdfEntries])

  const { markupLayers, forceRefresh: refreshPersistedMarkupLayers } =
    useRolodexEntriesWithSyncedRefreshes(
      component?.id && inspectionId
        ? {
            type: RolodexEntryType.MARKUP_LAYER,
            tags: {
              [RolodexEntryType.COMPONENT]: component.id,
              [RolodexEntryType.INSPECTION]: inspectionId,
            },
          }
        : undefined
    )

  // Flaw markup layers carry no `indication-set` tag and are queried by
  // component+inspection only, so also scope them to the current indications
  // (by their `indication` tag) -- this drops orphaned/cross-ticket flaw layers
  // that would otherwise render and clutter the panel.
  const indicationIdSet = useMemo(() => new Set(indicationIds), [indicationIds])
  const flawMarkupLayers = useMemo(
    () =>
      ltaSetId
        ? [...markupLayers]
            .filter((layer) => {
              const indicationId = getStringTag(
                layer.tags,
                RolodexEntryType.INDICATION
              )
              return (
                isFfsLtaPathMarkupLayer(layer, ltaSetId) &&
                !!indicationId &&
                indicationIdSet.has(indicationId)
              )
            })
            .sort(GeckoMarkupLayer.DefaultSort)
        : [],
    [ltaSetId, markupLayers, indicationIdSet]
  )

  const layers = useMemo(
    () =>
      buildFfsMarkupLayers({
        indications,
        selectedIndicationId,
        ctps,
        rulers,
        cdfsById,
        primaryCdf,
        component,
        flawMarkupLayers,
        componentBounds,
        cdfFamily,
      }),
    [
      indications,
      selectedIndicationId,
      ctps,
      rulers,
      cdfsById,
      primaryCdf,
      component,
      flawMarkupLayers,
      componentBounds,
      cdfFamily,
    ]
  )

  useEffect(() => {
    setFfsMarkupLayers(layers)
    return () => setFfsMarkupLayers([])
  }, [layers, setFfsMarkupLayers])

  const forceRefresh = useCallback(
    async () =>
      await Promise.all([
        refreshCtps(),
        refreshRulers(),
        refreshCtpCdfs(),
        refreshPersistedMarkupLayers(),
      ]),
    [refreshCtps, refreshRulers, refreshCtpCdfs, refreshPersistedMarkupLayers]
  )

  return useMemo(() => ({ forceRefresh }), [forceRefresh])
}
